import * as Dialog from "@radix-ui/react-dialog";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, CircleHelp, Compass, Lightbulb, X } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { Button, cx } from "./ui";
import { api, type Me } from "@/lib/api";
import { helpFor, TOUR, TOUR_ID, TOUR_VERSION, type TourStep } from "@/lib/guide";

import { t } from "@/lib/i18n";
interface GuideCtx {
  startTour: () => void;
  openHelp: () => void;
}
const GuideContext = createContext<GuideCtx>({ startTour: () => {}, openHelp: () => {} });
export const useGuide = () => useContext(GuideContext);

/** sichtbares Element mit dieser Markierung (Desktop-Leiste oder mobile Kopfzeile) */
function findAnchor(anchor: string): HTMLElement | null {
  for (const el of document.querySelectorAll<HTMLElement>(`[data-tour="${CSS.escape(anchor)}"]`)) if (el.getClientRects().length) return el;
  return null;
}

/** Rundgang und Hilfe für das ganze Portal; startet beim ersten Login von selbst */
export function GuideProvider({ me, children }: { me: Me; children: ReactNode }) {
  const qc = useQueryClient();
  const [steps, setSteps] = useState<TourStep[] | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const startTour = useCallback(() => {
    setHelpOpen(false);
    // nur Stationen, deren Ziel es für diese Person gibt (Rolle, Rechte, gebuchte Module)
    setTimeout(() => setSteps(TOUR.filter((s) => (!s.staffOnly || me.user.isStaff) && (!s.anchor || document.querySelector(`[data-tour="${CSS.escape(s.anchor)}"]`)))), 50);
  }, [me.user.isStaff]);

  const finish = useCallback(() => {
    setSteps(null);
    if ((me.onboarding?.[TOUR_ID] ?? 0) >= TOUR_VERSION) return;
    qc.setQueryData<Me>(["me"], (m) => (m ? { ...m, onboarding: { ...(m.onboarding ?? {}), [TOUR_ID]: TOUR_VERSION } } : m));
    void api.onboardingDone(TOUR_ID, TOUR_VERSION).catch(() => {});
  }, [me.onboarding, qc]);

  // erster Login (oder neue Rundgang-Version): einmal von selbst
  useEffect(() => {
    if ((me.onboarding?.[TOUR_ID] ?? 0) >= TOUR_VERSION) return;
    const t = setTimeout(startTour, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({ startTour, openHelp: () => setHelpOpen(true) }), [startTour]);
  return (
    <GuideContext.Provider value={value}>
      {children}
      {steps && steps.length > 0 && <Tour steps={steps} onClose={finish} />}
      <HelpSheet open={helpOpen} onOpenChange={setHelpOpen} onTour={startTour} />
    </GuideContext.Provider>
  );
}

function Tour({ steps, onClose: done }: { steps: TourStep[]; onClose: () => void }) {
  const [i, setI] = useState(0);
  const step = steps[i]!;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Seite, auf der der Rundgang begann – danach geht es dorthin zurück
  const [start] = useState(pathname);
  const onClose = useCallback(() => {
    navigate(start);
    done();
  }, [navigate, start, done]);

  // zu jedem Menüpunkt auch gleich die Seite zeigen
  const page = step.anchor?.startsWith("nav:") ? step.anchor.slice(4) : null;
  useEffect(() => {
    if (page) navigate(page);
  }, [page, navigate]);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const last = i === steps.length - 1;

  useLayoutEffect(() => {
    const el = step.anchor ? findAnchor(step.anchor) : null;
    if (!el) {
      setRect(null);
      return;
    }
    el.scrollIntoView({ block: "nearest" });
    const update = () => setRect(el.getBoundingClientRect());
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [step]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" || e.key === "Enter") (last ? onClose() : setI((x) => x + 1));
      if (e.key === "ArrowLeft") setI((x) => Math.max(0, x - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [last, onClose]);

  // Karte neben dem Ziel (rechts, sonst darunter), ohne Ziel in der Mitte
  const W = 340;
  const pad = 8;
  const pos: React.CSSProperties = rect
    ? rect.right + W + 24 < window.innerWidth
      ? { left: rect.right + 16, top: Math.max(16, Math.min(rect.top - 8, window.innerHeight - 260)) }
      : { left: Math.max(16, Math.min(rect.left, window.innerWidth - W - 16)), top: Math.min(rect.bottom + 12, window.innerHeight - 260) }
    : page
      ? { left: "50%", bottom: 16, transform: "translateX(-50%)" }
      : { left: "50%", top: "50%", transform: "translate(-50%, -50%)" };
  // mobile: Ziel ist im Menü versteckt → Hinweis, wo es zu finden ist
  const hiddenInMenu = !!step.anchor && !rect && step.anchor.startsWith("nav:");

  return (
    // pointer-events: offene Radix-Dialoge (z. B. Auftragsansicht) sperren Klicks außerhalb – der Rundgang muss trotzdem bedienbar sein
    <div className="fixed inset-0 z-[100]" style={{ pointerEvents: "auto" }} role="dialog" aria-modal="true" aria-label={t("Rundgang: {title}", { title: t(step.title) })}>
      {rect ? (
        <div
          className="absolute rounded-lg ring-2 ring-coral transition-all duration-200 pointer-events-none"
          style={{ left: rect.left - pad / 2, top: rect.top - pad / 2, width: rect.width + pad, height: rect.height + pad, boxShadow: `0 0 0 9999px rgba(10,10,10,${page ? 0.3 : 0.45})` }}
        />
      ) : (
        <div className={cx("absolute inset-0", page ? "bg-black/20" : "bg-black/45")} />
      )}
      <div className="absolute w-[min(360px,calc(100vw-32px))] overflow-hidden rounded-2xl border border-border bg-surface p-4 pt-5 shadow-2xl animate-fade-in" style={pos}>
        <div className="absolute inset-x-0 top-0 h-1 bg-surface-2" aria-hidden>
          <div className="h-full bg-coral transition-all duration-300" style={{ width: `${((i + 1) / steps.length) * 100}%` }} />
        </div>
        <div className="flex items-start gap-2">
          <p className="flex-1 font-semibold text-[15px] leading-snug">{t(step.title)}</p>
          <button onClick={onClose} className="size-7 -mr-1 -mt-1 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-fg" aria-label={t("Rundgang beenden")}><X className="size-4" /></button>
        </div>
        <p className="mt-1.5 text-[13px] text-fg-muted leading-relaxed">{t(step.body)}</p>
        {hiddenInMenu && <p className="mt-1.5 text-[12px] text-fg-subtle">{t("Zu finden im Menü ☰ oben links.")}</p>}
        <div className="mt-4 flex items-center gap-2">
          <span className="text-[12px] tabular-nums text-fg-subtle" aria-label={t("Schritt {v} von {length}", { v: i + 1, length: steps.length })}>
            {i + 1} / {steps.length}
          </span>
          <span className="ml-auto flex shrink-0 gap-1.5">
            {i === 0 ? (
              <Button size="sm" variant="ghost" onClick={onClose}>{t("Überspringen")}</Button>
            ) : (
              <Button size="sm" variant="ghost" icon={<ArrowLeft className="size-3.5" />} onClick={() => setI(i - 1)} aria-label={t("Zurück")} />
            )}
            <Button size="sm" variant="primary" onClick={() => (last ? onClose() : setI(i + 1))}>
              {i === 0 ? t("Los geht's") : last ? t("Fertig") : <>{t("Weiter")} <ArrowRight className="size-3.5 ml-1 inline" /></>}
            </Button>
          </span>
        </div>
      </div>
    </div>
  );
}

/** „Kurz erklärt“: Hilfe zur aktuellen Seite und Rundgang neu starten */
function HelpSheet({ open, onOpenChange, onTour }: { open: boolean; onOpenChange: (o: boolean) => void; onTour: () => void }) {
  const { pathname } = useLocation();
  const help = helpFor(pathname);
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/20 animate-fade-in" />
        <Dialog.Content className="fixed z-50 right-0 top-0 h-full w-[min(420px,100vw)] bg-surface border-l border-border shadow-2xl flex flex-col animate-sheet-in outline-none">
          <div className="px-5 pt-5 pb-4 border-b border-border flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[11.5px] font-semibold uppercase tracking-wide text-fg-subtle">{t("Kurz erklärt")}</p>
              <Dialog.Title className="text-[18px] font-semibold mt-0.5">{help?.title ?? t("Hilfe")}</Dialog.Title>
            </div>
            <Dialog.Close className="size-8 -mr-2 grid place-items-center rounded-lg text-fg-subtle hover:bg-surface-2 hover:text-fg" aria-label={t("Schließen")}><X className="size-4" /></Dialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
            {help ? (
              <>
                <Dialog.Description className="text-[13.5px] leading-relaxed">{t(help.intro)}</Dialog.Description>
                <ul className="space-y-2">
                  {help.points.map((p) => (
                    <li key={p} className="flex gap-2.5 text-[13px] text-fg-muted leading-relaxed"><span className="mt-2 size-1.5 shrink-0 rounded-full bg-coral" />{t(p)}</li>
                  ))}
                </ul>
                {help.tips?.map((tip) => (
                  <p key={tip} className="flex gap-2 rounded-lg bg-surface-2/70 px-3 py-2.5 text-[12.5px] text-fg-muted"><Lightbulb className="size-4 shrink-0 text-warning" />{t(tip)}</p>
                ))}
              </>
            ) : (
              <Dialog.Description className="text-[13.5px] text-fg-muted">{t("Zu dieser Seite gibt es noch keine Erklärung – der Rundgang zeigt dir die wichtigsten Bereiche.")}</Dialog.Description>
            )}
          </div>
          <div className="px-5 py-4 border-t border-border space-y-2">
            <Button className="w-full" icon={<Compass className="size-4" />} onClick={onTour}>{t("Rundgang starten")}</Button>
            <p className="text-[12px] text-fg-subtle text-center">{t("Suchen mit Strg/⌘ + K · Fragen? Einfach eurem Ansprechpartner bei Logiful schreiben.")}</p>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** kleiner „?“-Knopf (Seitenleiste, Kopfzeile, Seitentitel) */
export function HelpButton({ className, label, tourAnchor }: { className?: string; label?: boolean; tourAnchor?: string }) {
  const { openHelp } = useGuide();
  return (
    <button onClick={openHelp} data-tour={tourAnchor} className={cx("inline-flex items-center gap-1.5 rounded-lg text-fg-muted hover:text-fg hover:bg-surface-2", label ? "h-8 px-2.5 text-[13px]" : "size-8 justify-center", className)} aria-label={t("Hilfe: Kurz erklärt und Rundgang")} title={t("Kurz erklärt · Rundgang")}>
      <CircleHelp className="size-4" />
      {label && t("Hilfe")}
    </button>
  );
}
